import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  Stethoscope, 
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { appointmentsApi, patientsApi, interventionsApi } from '../services/api';
import InterventionDetailModal from '../components/InterventionDetailModal';
import { Intervention } from '../types';

const Dashboard: React.FC = () => {
  // Estados para el modal de detalles de intervención
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);
  const [isInterventionModalOpen, setIsInterventionModalOpen] = useState(false);
  // Estados para la paginación de intervenciones
  const [visibleInterventions, setVisibleInterventions] = useState(5);
  const [showAllInterventions, setShowAllInterventions] = useState(false);

  // Fetch today's appointments
  const { data: todayAppointments = [] } = useQuery(
    ['today-appointments'],
    async () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      const response = await appointmentsApi.getCalendar(
        start.toISOString(),
        end.toISOString()
      );
      // response.data is already the array of appointments
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  // Fetch patients count
  const { data: patientsData } = useQuery(
    ['patients-count'],
    async () => {
      const response = await patientsApi.getAll({ limit: 1 });
      // response.data contains { patients: [...], total: number }
      return response.data;
    },
  );

  // Fetch recent interventions
  const { data: interventionsData } = useQuery(
    ['recent-interventions'],
    async () => {
      const response = await interventionsApi.getAll({ limit: 20 });
      // response.data is the array of interventions
      return Array.isArray(response.data) ? response.data : [];
    },
  );

  // Extract values from responses
  const totalPatients = patientsData?.total ?? 0;
  const recentInterventions = interventionsData ?? [];

  const today = new Date();
  const todayStr = today.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const getStatusCounts = () => {
    const counts = {
      scheduled: 0,
      confirmed: 0,
      completed: 0,
      cancelled: 0,
    };

    todayAppointments.forEach((appointment: any) => {
      counts[appointment.status as keyof typeof counts]++;
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  // Funciones para el modal de intervenciones
  const handleInterventionClick = (intervention: Intervention) => {
    setSelectedIntervention(intervention);
    setIsInterventionModalOpen(true);
  };

  const handleInterventionModalClose = () => {
    setIsInterventionModalOpen(false);
    setSelectedIntervention(null);
  };

  // Funciones para manejar la expansión de intervenciones
  const handleShowMoreInterventions = () => {
    setShowAllInterventions(true);
    setVisibleInterventions(recentInterventions.length);
  };

  const handleShowLessInterventions = () => {
    setShowAllInterventions(false);
    setVisibleInterventions(5);
  };

  // Obtener las intervenciones a mostrar
  const displayedInterventions = recentInterventions.slice(0, visibleInterventions);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'scheduled': 'text-yellow-600 bg-yellow-100',
      'confirmed': 'text-blue-600 bg-blue-100',
      'completed': 'text-green-600 bg-green-100',
      'cancelled': 'text-red-600 bg-red-100',
      'no-show': 'text-orange-600 bg-orange-100',
    };
    return colors[status as keyof typeof colors] || 'text-gray-600 bg-gray-100';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="h-4 w-4" />;
      case 'confirmed':
        return <CheckCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      case 'no-show':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Resumen de la clínica dental</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:desktop-gap">
        <div className="desktop-card bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5 lg:desktop-padding">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-6 w-6 text-dental-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate lg:desktop-text-sm">
                    Citas Hoy
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 lg:desktop-text-lg">
                    {todayAppointments.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 lg:desktop-padding">
            <div className="text-sm lg:desktop-text-sm">
              <Link to="/calendar" className="font-medium text-dental-600 hover:text-dental-500">
                Ver calendario
              </Link>
            </div>
          </div>
        </div>

        <div className="desktop-card bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5 lg:desktop-padding">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate lg:desktop-text-sm">
                    Total Pacientes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 lg:desktop-text-lg">
                    {totalPatients}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/patients" className="font-medium text-green-600 hover:text-green-500">
                Ver pacientes
              </Link>
            </div>
          </div>
        </div>

        <div className="desktop-card bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5 lg:desktop-padding">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Stethoscope className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate lg:desktop-text-sm">
                    Intervenciones Recientes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 lg:desktop-text-lg">
                    {recentInterventions.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 lg:desktop-padding">
            <div className="text-sm lg:desktop-text-sm">
              <span className="font-medium text-purple-600">
                Últimas 5
              </span>
            </div>
          </div>
        </div>

        <div className="desktop-card bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5 lg:desktop-padding">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate lg:desktop-text-sm">
                    Citas Completadas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 lg:desktop-text-lg">
                    {statusCounts.completed}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 lg:desktop-padding">
            <div className="text-sm lg:desktop-text-sm">
              <span className="font-medium text-orange-600">
                Hoy
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:desktop-gap">
        {/* Today's Appointments */}
        <div className="desktop-card bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 lg:desktop-padding">
            <h3 className="text-lg font-medium text-gray-900 lg:desktop-text-lg">
              Citas de Hoy - {todayStr}
            </h3>
          </div>
          <div className="p-6">
            {todayAppointments.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No hay citas programadas para hoy</p>
            ) : (
              <div className="space-y-4">
                {todayAppointments.map((appointment: any) => (
                  <div key={appointment.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${getStatusColor(appointment.status)}`}>
                        {getStatusIcon(appointment.status)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {appointment.patient?.firstName || 'N/A'} {appointment.patient?.lastName || ''}
                        </p>
                        <p className="text-sm text-gray-500">{appointment.title}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {formatTime(appointment.startTime || appointment.start_time || appointment.start)}
                      </p>
                      <p className="text-sm text-gray-500">{appointment.dentist}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-gray-50 px-6 py-3">
            <Link
              to="/calendar"
              className="text-sm font-medium text-dental-600 hover:text-dental-500"
            >
              Ver todas las citas →
            </Link>
          </div>
        </div>

        {/* Recent Interventions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Intervenciones Recientes
            </h3>
          </div>
          <div className="p-6">
            {recentInterventions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No hay intervenciones recientes</p>
            ) : (
              <>
                <div className={`space-y-4 ${showAllInterventions ? 'max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100' : ''}`}>
                  {displayedInterventions.map((intervention: any) => (
                  <button
                    key={intervention._id}
                    onClick={() => handleInterventionClick(intervention)}
                    className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-dental-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-dental-500 focus:ring-offset-2"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full bg-dental-100">
                        <Stethoscope className="h-4 w-4 text-dental-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900">
                          {intervention.type.replace('_', ' ').toUpperCase()}
                        </p>
                        <p className="text-sm text-gray-500">{intervention.dentist}</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center space-x-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(intervention.createdAt).toLocaleDateString('es-ES')}
                        </p>
                        {intervention.cost && (
                          <p className="text-sm text-gray-500">{intervention.cost}€</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </button>
                  ))}
                </div>
                
                {/* Botones para mostrar más/menos intervenciones */}
                {recentInterventions.length > 5 && (
                  <div className="pt-4 border-t border-gray-200">
                    {!showAllInterventions ? (
                      <button
                        onClick={handleShowMoreInterventions}
                        className="w-full flex items-center justify-center space-x-2 py-2 px-4 text-sm font-medium text-dental-600 hover:text-dental-700 hover:bg-dental-50 rounded-md transition-colors duration-200"
                      >
                        <span>Ver más intervenciones ({recentInterventions.length - 5} adicionales)</span>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={handleShowLessInterventions}
                        className="w-full flex items-center justify-center space-x-2 py-2 px-4 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200"
                      >
                        <span>Mostrar menos</span>
                        <ChevronUp className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="bg-gray-50 px-6 py-3">
            <span className="text-sm font-medium text-dental-600">
              {showAllInterventions ? `${recentInterventions.length} intervenciones` : 'Últimas 5 intervenciones'}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Acciones Rápidas</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/calendar"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Calendar className="h-8 w-8 text-dental-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Nueva Cita</p>
                <p className="text-sm text-gray-500">Programar una cita</p>
              </div>
            </Link>
            
            <Link
              to="/patients"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Users className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Nuevo Paciente</p>
                <p className="text-sm text-gray-500">Registrar paciente</p>
              </div>
            </Link>
            
            <Link
              to="/appointments"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Stethoscope className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Ver Citas</p>
                <p className="text-sm text-gray-500">Gestionar citas</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Modal de detalles de intervención */}
      {isInterventionModalOpen && (
        <InterventionDetailModal
          isOpen={isInterventionModalOpen}
          onClose={handleInterventionModalClose}
          intervention={selectedIntervention}
        />
      )}
    </div>
  );
};

export default Dashboard;















